'use client'

import Link from "next/link"
import { useState } from "react"
import {
  Menu,
  X,
  Phone,
  MapPin,
  Clock,
  ShoppingBag,
  ChevronRight,
  Globe2,
  Crown,
  Award,
  Sparkles,
  Search,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetHeader,
} from "@/components/ui/sheet"
import { SITE_CONFIG } from "@/lib/site-config"
import { useCart } from "@/lib/cart-store"
import { CartDrawer } from "./cart-drawer"

const navLinks = [
  { href: "#hero", label: "Accueil" },
  { href: "#rayons", label: "Rayons" },
  { href: "/collection-privee", label: "Collection Privilège" },
  { href: "/services-luxe", label: "Services de Luxe" },
  { href: "#histoire", label: "Notre Histoire" },
  { href: "/boutique-eva-diaw", label: "Maison Eva Diaw" },
  { href: "#contact", label: "Contact" },
]

// Lien de recherche (icône seule sur desktop, label + icône sur mobile)
const searchLink = { href: "/recherche", label: "Recherche" }

export function SiteHeader() {
  const [open, setOpen] = useState(false)
  const count = useCart((s) =>
    s.items.reduce((sum, i) => sum + i.quantity, 0)
  )

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Strate 1 — Filet d'or supérieur */}
      <div className="h-[3px] w-full gradient-gold" />
      <div className="h-px w-full bg-gold/40" />

      {/* Strate 2 — Bandeau utilitaire */}
      <div className="hidden md:block gradient-cuir text-primary-foreground/80">
        <div className="container mx-auto flex h-9 items-center justify-between px-4 text-[11px] tracking-wide">
          <div className="flex items-center gap-6">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-gold" />
              {SITE_CONFIG.address.line1}, {SITE_CONFIG.address.zip}{" "}
              {SITE_CONFIG.address.city}
            </span>
            <span className="hidden lg:inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3 text-gold" />
              {SITE_CONFIG.contact.hours}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href={`tel:${SITE_CONFIG.contact.phone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-1.5 hover:text-gold transition-colors"
            >
              <Phone className="h-3 w-3 text-gold" />
              {SITE_CONFIG.contact.phone}
            </a>
            <Link
              href="/boutique-eva-diaw"
              className="inline-flex items-center gap-1 hover:text-gold transition-colors"
            >
              <Crown className="h-3 w-3 text-gold" />
              Maison Eva Diaw
              <ChevronRight className="h-3 w-3" />
            </Link>
            <Link
              href="/collection-privee"
              className="hidden lg:inline-flex items-center gap-1 hover:text-gold transition-colors"
            >
              <Sparkles className="h-3 w-3 text-gold" />
              Collection Privilège
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* Strate 3 — Corps principal */}
      <div className="glass-gold border-b border-gold/40 shadow-luxury">
        <div className="container mx-auto flex h-20 md:h-24 items-center justify-between px-4">
          {/* Logo héraldique */}
          <Link href="#hero" className="flex items-center gap-3 md:gap-4 group">
            <div className="relative h-14 w-14 md:h-16 md:w-16 flex-shrink-0">
              {/* Anneau extérieur or */}
              <div className="absolute inset-0 rounded-full gradient-gold p-[3px] transition-transform group-hover:scale-105">
                <div className="h-full w-full rounded-full bg-cuir p-[2px]">
                  <div className="h-full w-full rounded-full border border-gold/60 p-[3px]">
                    <div className="h-full w-full rounded-full bg-gradient-to-br from-amber-50 to-amber-100/80 flex items-center justify-center relative overflow-hidden">
                      <span className="font-display text-2xl md:text-3xl font-bold text-gradient-gold">
                        S
                      </span>
                      {/* Cabochons cardinaux */}
                      <span className="absolute top-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-gold" />
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-gold" />
                      <span className="absolute left-1 top-1/2 -translate-y-1/2 h-1 w-1 rounded-full bg-gold" />
                      <span className="absolute right-1 top-1/2 -translate-y-1/2 h-1 w-1 rounded-full bg-gold" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-2xl md:text-[28px] font-semibold tracking-[0.18em] text-cuir">
                SADIYA
              </span>
              <span className="flex items-center gap-2 text-[10px] md:text-[11px] uppercase tracking-[0.42em] text-gold font-medium">
                Style Cuir
                <span className="h-px w-4 bg-gold/70" />
                Paris
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className="relative text-sm font-medium text-cuir hover:text-gold transition-colors group"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-to-r from-gold via-gold to-emerald transition-all duration-400 group-hover:w-full" />
              </Link>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={`tel:${SITE_CONFIG.contact.phone.replace(/\s/g, "")}`}
              className="hidden xl:flex items-center gap-2 text-sm text-cuir hover:text-gold transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden 2xl:inline">
                {SITE_CONFIG.contact.phone}
              </span>
            </a>

            {/* Search */}
            <Button
              variant="outline"
              size="sm"
              asChild
              className="relative border-gold/50 text-cuir hover:bg-gold/10 hover:text-cuir rounded-full h-10 w-10 p-0"
            >
              <Link href={searchLink.href} aria-label="Rechercher dans le catalogue">
                <Search className="h-4 w-4" />
              </Link>
            </Button>

            {/* Cart */}
            <Button
              variant="outline"
              size="sm"
              asChild
              className="relative border-gold/50 text-cuir hover:bg-gold/10 hover:text-cuir rounded-full h-10 w-10 p-0"
            >
              <Link href="/panier" aria-label="Voir le panier">
                <ShoppingBag className="h-4 w-4" />
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full gradient-cuir text-gold text-[10px] font-bold flex items-center justify-center border border-gold/60 shadow-gold">
                    {count}
                  </span>
                )}
              </Link>
            </Button>

            {/* CTA */}
            <Button
              asChild
              size="sm"
              className="hidden md:flex gradient-emerald-gold border border-gold/60 text-primary-foreground hover:opacity-90 shadow-luxury rounded-full"
            >
              <Link href="#contact">
                <Award className="mr-2 h-4 w-4" />
                Nous contacter
              </Link>
            </Button>

            {/* Mobile menu trigger */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-cuir hover:bg-gold/10 rounded-full"
                  aria-label="Menu"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[340px] sm:w-[400px] bg-background p-0 overflow-y-auto"
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>Menu de navigation</SheetTitle>
                </SheetHeader>

                {/* Enseigne héraldique mobile */}
                <div className="gradient-cuir px-6 pt-8 pb-6 relative">
                  <button
                    onClick={() => setOpen(false)}
                    className="absolute top-4 right-4 h-9 w-9 rounded-full bg-gold/10 hover:bg-gold/20 text-gold flex items-center justify-center transition-colors"
                    aria-label="Fermer le menu"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-12 w-12 rounded-full gradient-gold p-[2px]">
                      <div className="h-full w-full rounded-full bg-cuir flex items-center justify-center">
                        <span className="font-display text-xl font-bold text-gradient-gold">
                          S
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col leading-tight">
                      <span className="font-display text-xl font-semibold tracking-[0.18em] text-cuir">
                        SADIYA
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                        Style Cuir · Paris
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-primary-foreground/70 italic">
                    Maison de prestige depuis 2019
                  </p>
                </div>

                {/* Nav */}
                <nav className="px-4 py-6 flex flex-col gap-1">
                  {/* Recherche — en premier, mise en évidence */}
                  <Link
                    href={searchLink.href}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-gold/5 border border-gold/30 text-cuir hover:bg-gold/15 hover:text-gold transition-colors mb-2"
                  >
                    <span className="font-display text-base flex items-center gap-2">
                      <Search className="h-4 w-4 text-gold" />
                      Rechercher dans le catalogue
                    </span>
                    <ChevronRight className="h-4 w-4 text-gold/60" />
                  </Link>

                  {navLinks.map((l) => (
                    <Link
                      key={l.href}
                      href={l.href}
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between px-4 py-3 rounded-lg text-cuir hover:bg-gold/10 hover:text-gold transition-colors"
                    >
                      <span className="font-display text-base">{l.label}</span>
                      <ChevronRight className="h-4 w-4 text-gold/60" />
                    </Link>
                  ))}
                </nav>

                {/* Maison card */}
                <div className="mx-4 mb-4 p-5 rounded-xl gradient-cuir text-primary-foreground">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
                    Maison SADIYA
                  </p>
                  <div className="space-y-3 text-sm">
                    <a
                      href={`tel:${SITE_CONFIG.contact.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-2 hover:text-gold transition-colors"
                    >
                      <Phone className="h-4 w-4 text-gold" />
                      {SITE_CONFIG.contact.phone}
                    </a>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gold mt-0.5" />
                      <span>
                        {SITE_CONFIG.address.line1}
                        <br />
                        {SITE_CONFIG.address.zip} {SITE_CONFIG.address.city}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gold" />
                      {SITE_CONFIG.contact.hours}
                    </div>
                  </div>
                </div>

                {/* Curatrice card */}
                <div className="mx-4 mb-6 p-5 rounded-xl border border-gold/30 bg-gold/5">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">
                    Curatrice officielle
                  </p>
                  <Link
                    href="/boutique-eva-diaw"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <p className="font-display text-lg text-cuir">
                        Maison Eva Diaw
                      </p>
                      <p className="text-xs text-foreground/60">
                        Curation mode & art de vivre
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gold" />
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Strate 4 — Filet inférieur */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-gold/70 to-transparent" />
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-emerald/40 to-transparent" />

      {/* Cart drawer (mounted once) */}
      <CartDrawer />
    </header>
  )
}
